// Reuse Creative in Campaign Modal
import React, { useState, useEffect } from 'react';
import { creativeLibraryApi } from '../../lib/creativeLibraryApi';
import { campaignsApi } from '../../lib/campaignsApi';
import type { Creative } from '../../types/creative-library';

interface ReuseCreativeModalProps {
  creative: Creative;
  onClose: () => void;
  onSuccess?: (campaignId: string, newAdId: string) => void;
}

interface CampaignOption {
  id: string;
  name: string;
  platform: string;
  status: string;
  budget: number;
}

export const ReuseCreativeModal: React.FC<ReuseCreativeModalProps> = ({
  creative,
  onClose,
  onSuccess
}) => {
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [budget, setBudget] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<'campaign' | 'draft'>('campaign');

  useEffect(() => {
    loadCampaigns();
  }, [creative.platform]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      // Get campaigns for the same platform
      const campaignList = await campaignsApi.list({
        platform: creative.platform,
        status: 'active'
      });
      // Map to CampaignOption interface
      setCampaigns(campaignList as any);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'campaign' && !selectedCampaignId) {
      alert('Please select a campaign');
      return;
    }

    try {
      setSubmitting(true);
      
      if (mode === 'campaign') {
        // Reuse in existing campaign
        const result = await creativeLibraryApi.reuseInCampaign(
          creative.id,
          selectedCampaignId,
          budget ? parseFloat(budget) : undefined
        );
        
        onSuccess?.(result.campaign_id, result.new_ad_id);
        onClose();
      } else {
        // Save to drafts (not yet implemented in backend, but structure is ready)
        alert('Draft functionality coming soon!');
      }
    } catch (error) {
      console.error('Failed to reuse creative:', error);
      alert('Failed to reuse creative. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-boxdark rounded-lg max-w-2xl w-full">
        {/* Header */}
        <div className="border-b border-stroke dark:border-strokedark px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-black dark:text-white">
            Reuse Creative in Campaign
          </h2>
          <button
            onClick={onClose}
            className="text-bodydark hover:text-black dark:text-bodydark1 dark:hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Creative Preview */}
        <div className="px-6 py-4 border-b border-stroke dark:border-strokedark">
          <div className="flex items-center gap-4">
            {creative.thumbnail_url || creative.asset_url ? (
              <img
                src={creative.thumbnail_url || creative.asset_url}
                alt={creative.name}
                className="w-24 h-24 object-cover rounded-lg"
              />
            ) : (
              <div className="w-24 h-24 bg-gray-200 dark:bg-meta-4 rounded-lg flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-black dark:text-white">{creative.name}</h3>
              <div className="flex items-center gap-3 mt-1 text-sm text-bodydark dark:text-bodydark1">
                <span className="capitalize">{creative.platform}</span>
                <span>•</span>
                <span className="capitalize">{creative.creative_type}</span>
                <span>•</span>
                <span>ROAS: {creative.roas.toFixed(2)}x</span>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-black dark:text-white mb-3">
              Choose Action
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode('campaign')}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  mode === 'campaign'
                    ? 'border-primary bg-primary bg-opacity-10 text-primary'
                    : 'border-stroke dark:border-strokedark hover:border-primary'
                }`}
              >
                <div className="font-medium">Add to Campaign</div>
                <div className="text-xs mt-1 opacity-75">Launch immediately</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('draft')}
                className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                  mode === 'draft'
                    ? 'border-primary bg-primary bg-opacity-10 text-primary'
                    : 'border-stroke dark:border-strokedark hover:border-primary'
                }`}
              >
                <div className="font-medium">Save as Draft</div>
                <div className="text-xs mt-1 opacity-75">Review later</div>
              </button>
            </div>
          </div>

          {mode === 'campaign' && (
            <>
              {/* Campaign Selector */}
              <div>
                <label className="block text-sm font-medium text-black dark:text-white mb-2">
                  Select Campaign
                </label>
                {loading ? (
                  <div className="text-sm text-bodydark dark:text-bodydark1">Loading campaigns...</div>
                ) : campaigns.length === 0 ? (
                  <div className="text-sm text-bodydark dark:text-bodydark1">
                    No active {creative.platform} campaigns found. Please create a campaign first.
                  </div>
                ) : (
                  <select
                    value={selectedCampaignId}
                    onChange={(e) => setSelectedCampaignId(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white"
                  >
                    <option value="">-- Select a campaign --</option>
                    {campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name} ({campaign.status})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Selected Campaign Info */}
              {selectedCampaign && (
                <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-bodydark dark:text-bodydark1">Campaign Budget:</span>
                    <span className="font-medium text-black dark:text-white">
                      ${selectedCampaign.budget.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-bodydark dark:text-bodydark1">Platform:</span>
                    <span className="font-medium text-black dark:text-white capitalize">
                      {selectedCampaign.platform}
                    </span>
                  </div>
                </div>
              )}

              {/* Budget Override */}
              <div>
                <label className="block text-sm font-medium text-black dark:text-white mb-2">
                  Ad Budget (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-bodydark dark:text-bodydark1">
                    $
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    placeholder="Leave empty to use campaign budget"
                    className="w-full pl-8 pr-4 py-3 border border-stroke dark:border-strokedark rounded-lg bg-white dark:bg-boxdark text-black dark:text-white"
                  />
                </div>
                <p className="text-xs text-bodydark dark:text-bodydark1 mt-1">
                  Specify a custom budget for this ad, or leave empty to use the campaign's budget settings
                </p>
              </div>
            </>
          )}

          {mode === 'draft' && (
            <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-1">Save as Draft</p>
                  <p>This creative will be saved to your drafts folder for later review. You can configure and launch it when you're ready.</p>
                </div>
              </div>
            </div>
          )}

          {/* Platform Compatibility Check */}
          {selectedCampaign && selectedCampaign.platform !== creative.platform && (
            <div className="bg-warning bg-opacity-10 border border-warning rounded-lg p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-warning">
                  <p className="font-medium mb-1">Platform Mismatch</p>
                  <p>This creative is from {creative.platform} but the selected campaign is for {selectedCampaign.platform}. The creative may need to be adapted.</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-stroke dark:border-strokedark">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-stroke dark:border-strokedark rounded-lg hover:bg-gray-50 dark:hover:bg-meta-4 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (mode === 'campaign' && (!selectedCampaignId || campaigns.length === 0))}
              className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : mode === 'campaign' ? (
                'Add to Campaign'
              ) : (
                'Save as Draft'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
